import { AxiosResponse } from 'axios';
import { useDispatch } from '@algoan/dashboard/store/store';
import { useAsync, useDownload } from '@algoan/dashboard/utils';
import { AsyncStatus, NotificationVariant } from '@algoan/dashboard/model';
import { notify } from '@algoan/dashboard/store/thunks';

export const convertBlobToUrl = (data: AxiosResponse<string>, type: string): string => {
  const file = new Blob([data.data], { type: type });
  return URL.createObjectURL(file);
};

type RunDownloadProps = {
  axiosRequest: () => Promise<AxiosResponse<string>>;
  fileName: string;
  type: string;
  onSuccess?: () => void;
};

type UseAsyncDownload = (props: RunDownloadProps) => Promise<void>;

export const useAsyncDownload = (): { downloadStatus: AsyncStatus; runDownload: UseAsyncDownload } => {
  const dispatch = useDispatch();
  const downloadFiles = useDownload();
  const { asyncStatus: downloadStatus, run } = useAsync<AxiosResponse<string>>();

  const runDownload = async ({ axiosRequest, fileName, type, onSuccess }: RunDownloadProps): Promise<void> => {
    run(axiosRequest())
      .then((data) => {
        if (!data) {
          throw new Error('No data returned from the download request');
        }

        const fileURL = convertBlobToUrl(data, type);

        downloadFiles(fileName, fileURL);
        dispatch(
          notify({
            type: NotificationVariant.SUCCESS,
            message: {
              id: 'export.success_notification',
              defaultMessage: 'The export has been successfully downloaded',
              description: 'Text of the export files success notification',
            },
          }),
        );
        if (onSuccess) onSuccess();
      })
      .catch(() => {
        dispatch(
          notify({
            type: NotificationVariant.ERROR,
            message: {
              id: 'export.error_notification',
              defaultMessage: 'The export has failed. Please try again',
              description: 'Text of the export files failure notification',
            },
          }),
        );
      });
  };

  return { downloadStatus, runDownload };
};
